/**
 * Tenant-scoped profile API.
 * Global identity: id, email, full_name
 * Tenant-scoped: display_name, job_title, company_name, profile_photo_url
 * Never use User.updateMyUserData() or User.me() fallback for tenant-scoped fields.
 */

import { User } from '@/api/entities';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

const EMPTY_PROFILE = {
  display_name: '',
  job_title: '',
  company_name: '',
  profile_photo_url: ''
};

export async function getProfileIdentity(opts = {}) {
  const base44User = await User.me();
  const adminTenant =
    base44User?.role === 'admin'
      ? (typeof localStorage !== 'undefined' ? localStorage.getItem('admin-active-tenant') : null)
      : null;
  const tenantId = resolveEffectiveTenant(
    adminTenant,
    opts.currentTenant ?? null,
    base44User,
    'instructure'
  );

  return {
    userId: base44User.id,
    tenantId,
    identity: {
      id: base44User.id,
      email: base44User.email,
      full_name: base44User.full_name
    }
  };
}

export async function fetchTenantProfile(opts = {}) {
  const { userId, tenantId, identity } = await getProfileIdentity(opts);

  try {
    const response = await fetch(`${API_BASE_URL}/api/user/me`, {
      headers: {
        'x-user-id': userId,
        'x-tenant-id': tenantId
      },
      credentials: 'include'
    });

    if (!response.ok) {
      if (!window.__profileApiFailLogged) {
        window.__profileApiFailLogged = true;
        console.warn('profileApi: Tenant profile fetch failed, using empty profile');
      }
      return { identity, profile: { ...EMPTY_PROFILE }, tenantId, userId };
    }

    const profile = await response.json();
    return {
      identity,
      profile: {
        display_name: profile.display_name || '',
        job_title: profile.job_title || '',
        company_name: profile.company_name || '',
        profile_photo_url: profile.profile_photo_url || ''
      },
      tenantId,
      userId
    };
  } catch (e) {
    if (!window.__profileApiFailLogged) {
      window.__profileApiFailLogged = true;
      console.warn('profileApi: Tenant profile fetch error, using empty profile');
    }
    return { identity, profile: { ...EMPTY_PROFILE }, tenantId, userId };
  }
}

export async function updateTenantProfile(payload) {
  const { userId, tenantId } = await getProfileIdentity();

  const response = await fetch(`${API_BASE_URL}/api/user/me`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-user-id': userId,
      'x-tenant-id': tenantId
    },
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error('Failed to update tenant profile');
  }

  return response.json();
}
